import React, { useState, useEffect, useRef } from 'react';
import { getBackendURL } from '../utils';
import { useNavigate } from 'react-router-dom';
import ProgressTabs from '../components/ProgressTabs';
import SweatTextTitle from '../steps/SweatTextTitle';
import RemindersInfo from './RemindersInfo';
import { useAuth } from '../auth';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/style.css';
import '../BasicReminder.css';

const Step2 = () => {
  const { user } = useAuth();
  const [phone, setPhone] = useState(user?.phone || '');
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(true);
  const [syncingPhone, setSyncingPhone] = useState(false);
  const [syncingConsent, setSyncingConsent] = useState(false);
  const [syncedPhone, setSyncedPhone] = useState(false);
  const [syncedConsent, setSyncedConsent] = useState(false);
  const [syncError, setSyncError] = useState('');
  const [error, setError] = useState('');
  const [userSettingsID, setUserSettingsID] = useState(null);
  const [isPhoneValid, setIsPhoneValid] = useState(true);
  const [testMsgStatus, setTestMsgStatus] = useState('');
  const [sendingTestMsg, setSendingTestMsg] = useState(false);
  const [testMsgSent, setTestMsgSent] = useState(false);
  const navigate = useNavigate();
  const debouncePhoneRef = useRef();
  const debounceConsentRef = useRef();

  useEffect(() => {
    if (!user || !user.role.includes('trainer')) {
      alert('You must be a trainer to access this step. If you are indeed a trainer, please contact our Instagram for account support.');
      navigate('/tools/reminders/step1');
      return;
    }
    // Fetch initial sms_opt_in setting
    const fetchSettings = async () => {
      try {
        const res = await fetch(`${getBackendURL()}/user_settings/?user_id=${user.user_id || user.id}`, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Authorization': `Bearer ${window.localStorage.getItem('ptc_access_token') || ''}`
          }
        });
        if (!res.ok) throw new Error('Failed to fetch settings');
        const datalist = await res.json();
        const data = datalist[0] || {};
        console.log('Fetched user settings:', data);

        setConsent(!!data.sms_opt_in);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, [user, navigate]);

  useEffect(() => {
    // Simple international phone validation: starts with + and at least 10 digits
    const digits = phone.replace(/\D/g, '');
    setIsPhoneValid(phone.startsWith('+') && digits.length >= 10);
  }, [phone]);

  // Debounced phone PUT
  useEffect(() => {
    if (phone === (user?.phone || '') || !isPhoneValid) return;
    if (debouncePhoneRef.current) clearTimeout(debouncePhoneRef.current);
    debouncePhoneRef.current = setTimeout(async () => {
      setSyncingPhone(true);
      setSyncedPhone(false);
      setSyncError('');
      try {
        const userId = user?.user_id || user?.id;
        const res = await fetch(`${getBackendURL()}/users/${userId}`, {
          method: 'PUT',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${window.localStorage.getItem('ptc_access_token') || ''}`
          },
          body: JSON.stringify({ phone }),
        });
        if (!res.ok) throw new Error('Failed to update phone');
        setSyncedPhone(true);
      } catch (err) {
        setSyncError(err.message);
      } finally {
        setSyncingPhone(false);
      }
    }, 2000);
    return () => clearTimeout(debouncePhoneRef.current);
  }, [phone, user, isPhoneValid]);

  // Debounced consent PUT
  useEffect(() => {
    if (loading) return;
    if (debounceConsentRef.current) clearTimeout(debounceConsentRef.current);
    debounceConsentRef.current = setTimeout(async () => {
      setSyncingConsent(true);
      setSyncedConsent(false);
      setSyncError('');
      try {
        const res = await fetch(`${getBackendURL()}/user_settings/${userSettingsID}`, {
          method: 'PUT',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${window.localStorage.getItem('ptc_access_token') || ''}`
          },
          body: JSON.stringify({ sms_opt_in: consent }),
        });
        if (!res.ok) throw new Error('Failed to update SMS opt-in');
        setSyncedConsent(true);
      } catch (err) {
        setSyncError(err.message);
      } finally {
        setSyncingConsent(false);
      }
    }, 2000);
    return () => clearTimeout(debounceConsentRef.current);
  }, [consent, user, loading]);

  const handleSendTestMessage = async () => {
    setTestMsgStatus('');
    setSendingTestMsg(true);
    try {
      const res = await fetch(`${getBackendURL()}/opt-in-trainer`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${window.localStorage.getItem('ptc_access_token') || ''}`
        },
        body: JSON.stringify({ phone }),
      });
      if (!res.ok) throw new Error('Failed to send test message');
      setTestMsgStatus('Test message sent!');
      setTestMsgSent(true);
    } catch (err) {
      setTestMsgStatus('Error: ' + err.message);
    } finally {
      setSendingTestMsg(false);
    }
  };

  return (
    <div className="reminder-container fogged-card">
      <SweatTextTitle />
      <RemindersInfo />
      <ProgressTabs currentStep={1} />

      <h2 className="step-title">Step 2: Trainer SMS Opt-In</h2>
      <p>As a trainer, you'll receive texts from our system.</p>
      <label>
        Please enter your phone number:
        <div style={{ margin: '1rem 0', display: 'flex', alignItems: 'center' }}>
          <div >
            <PhoneInput
              defaultCountry="us"
              value={phone}
              onChange={setPhone}
              disabled={syncingPhone}
              style={{
                background: !isPhoneValid ? '#ffb368ff' : undefined, // muted pink-grey
                border: !isPhoneValid ? 'solid 3px #ffb368ff' : undefined, // muted pink-grey
                borderRadius: '4px',
                width: '100%',
                boxSizing: 'border-box',
              }}
            />
          </div>
        </div>
      </label>
      {phone && (
        <div style={{ margin: '1rem 0', fontSize: '1rem' }}>
          <label>
            <input
              type="checkbox"
              checked={consent}
              onChange={e => setConsent(e.target.checked)}
              disabled={syncingConsent}
              style={{ marginRight: '0.5rem' }}
            />
            I agree to receive ongoing transactional and marketing SMS from SweatText
            and ThePersonalTrainersClub.com. 
            These may include appointment reminders or check-ins. 
            It may also include system 
            messages from this site about your account. 
            Message & data rates may apply. 
            Reply STOP to unsubscribe at any time.
          </label>
          <div style={{ marginTop: '1rem' }}>
            By opting in, you agree to our{' '}
            <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a> and{' '}
            <a href="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>.
            <br />
            You can also adjust preferences later via your account settings once you've created an account.
          </div>
        </div>
      )}
      <div style={{ margin: '1rem 0' }}>
        <button
          className="ptc-button"
          style={{ padding: '0.5rem 1rem', fontSize: '1rem', position: 'relative', minWidth: '180px' }}
          disabled={!isPhoneValid || !consent || sendingTestMsg || testMsgSent}
          onClick={handleSendTestMessage}
        >
          {sendingTestMsg ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
              <span className="spinner" style={{
                width: '1rem',
                height: '1rem',
                border: '2px solid #ccc',
                borderTop: '2px solid #000',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                marginRight: '0.5rem',
              }}></span>
              Sending...
            </span>
          ) : (
            'Send test message'
          )}
        </button>
        {testMsgStatus && (
          <div style={{ color: testMsgStatus.startsWith('Error') ? 'red' : 'green', marginTop: '0.5rem' }}>
            {testMsgStatus}
          </div>
        )}
      </div>

      {error && <div style={{ color: 'red', marginBottom: '1rem' }}>{error}</div>}
      {syncError && <div style={{ color: 'red', marginBottom: '1rem' }}>{syncError}</div>}

      <div style={{ margin: '1rem 0' }}>
        {(syncingPhone || syncingConsent) && (
          <span className="syncing-indicator" style={{ display: 'inline-flex', alignItems: 'center', marginLeft: '1rem' }}>
            <span className="spinner" style={{
              width: '1rem',
              height: '1rem',
              border: '2px solid #ccc',
              borderTop: '2px solid #000',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              marginRight: '0.5rem',
            }}></span>
            Syncing...
          </span>
        )}
        {(syncedPhone || syncedConsent) && <span style={{ color: 'green', fontWeight: 'bold', marginLeft: '1rem' }}>✔ Changes saved!</span>}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2rem' }}>
        <button className="ptc-secondary-button" onClick={() => navigate('/tools/reminders/step1')} style={{ padding: '1rem 2rem', fontSize: '1rem' }}>
          Previous Step
        </button>
        <button className="ptc-button" onClick={() => navigate('/tools/reminders/step3')} style={{ padding: '1rem 2rem', fontSize: '1rem' }}>
          Next Step
        </button>
      </div>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default Step2;